import React from 'react';
import { Panel, Glyphicon, Alert } from 'react-bootstrap';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import PropTypes from 'prop-types';
import includes from 'lodash/includes';
import filter from 'lodash/filter';
import SmallLoader from '../../../components/SmallLoader';
import {
    abnormalitiesFormatter,
    impactedBoxesFormatter,
    impactedKanbanBoxesFormatter,
    numberOfKanbansFormatter,
    palletsFormatter,
    recoveryActionsFormatter,
} from '../../../shared/utils/rowFormatters';
import { impactedBoxesValidator, palletsValidator } from '../../../shared/utils/rowValidators';
//import { canManifestBeEdited } from '../../../shared/utils/manifest';
import { formatEmc, formatSupplier } from '../../../shared/utils/run';
import { Link } from 'react-router';

const kanbanTable = kanbanTableOptions => abnManifestRow => {
    const {
        handleKanbanCellEdit,
        handleKanbanSelection,
        handleKanbanSelectionAll,
        selectedManifestIds,
        selectedKanbanIds,
    } = kanbanTableOptions;

    const isKanbanEditable =
        includes(selectedManifestIds, abnManifestRow.manifest.manifestId)/* &&
        canManifestBeEdited(abnManifestRow)*/;

    return (
        <div>
            <BootstrapTable
                data={abnManifestRow.kanbans}
                options={{
                    onCellEdit: handleKanbanCellEdit(abnManifestRow),
                }}
                cellEdit={{
                    mode: 'click',
                    blurToSave: true,
                }}
                selectRow={
                    isKanbanEditable
                        ? {
                              mode: 'checkbox',
                              onSelect: handleKanbanSelection,
                              onSelectAll: handleKanbanSelectionAll,
                              selected: selectedKanbanIds,
                              clickToSelect: true,
                              clickToExpand: true,
                          }
                        : {}
                }
            >
                <TableHeaderColumn dataField="kanbanId" isKey hidden>
                    ID
                </TableHeaderColumn>
                <TableHeaderColumn dataField="kanbans" editable={false}>
                    Kanban
                </TableHeaderColumn>
                <TableHeaderColumn dataField="totalNumberOfBoxes" editable={false}>
                    Total # of boxes
                </TableHeaderColumn>
                <TableHeaderColumn
                    dataField="impactedBoxes"
                    editable={{
                        readOnly: !isKanbanEditable,
                        validator: impactedBoxesValidator,
                    }}
                    dataFormat={impactedBoxesFormatter(isKanbanEditable)}
                >
                    Impacted # of boxes
                </TableHeaderColumn>
            </BootstrapTable>
        </div>
    );
};
const manifestNumberFormatter = (cell, row) => {
    if (row.manifestId) {
        return (
            <div>
                <p key={row.manifestId}>
                    <Link to={`manifests/${row.manifestId}`}>{row.manifestNumber}</Link>
                </p>
            </div>
        );
    }
};


const nonEditableRowsFilter = (abnormality, abnManifests, selectedManifestIds) => {
    let manifestsWithRecoveryActions = [];

    if (abnormality) {
        manifestsWithRecoveryActions = filter(
            abnormality.abnormalityManifests,
            am => am.recoveryActions && am.recoveryActions.length > 0
        ).map(abnMan => abnMan.manifestId);
    }

    return filter(
        abnManifests,
        am =>
            !includes(selectedManifestIds, am.manifest.manifestId) &&
            !includes(manifestsWithRecoveryActions, am.manifest.manifestId)
    ).map(am => am.manifest.manifestId);
};

class ManifestsInfoGeneric extends React.Component {
    render() {
        const {
            abnormality,
            abnManifests,
            errors,
            handleCellEdit,
            handleKanbanCellEdit,
            handleKanbanSelection,
            handleKanbanSelectionAll,
            handleManifestSelection,
            handleManifestSelectionAll,
            isEditable,
            isFetching,
            isSelectable,
            selectedKanbanIds,
            selectedManifestIds,
        } = this.props;

        const kanbanTableOptions = {
            handleKanbanCellEdit,
            handleKanbanSelection,
            handleKanbanSelectionAll,
            selectedManifestIds,
            selectedKanbanIds,
        };

        const flattenedAbnManifests = [];
        // Add manifestId to root level so we can use it in the table as key
        abnManifests.forEach(am =>
            flattenedAbnManifests.push({
                ...am,
                manifestId: am.manifest.manifestId,
                manifestNumber: am.manifest.manifestNumber,
                supplierCode: formatSupplier({
                    supplierCode: am.manifest.supplierCode,
                    supplierPlantCode: am.manifest.supplierPlantCode,
                }),
                emc: formatEmc({
                    emcCode: am.manifest.emcCode,
                    emcPlantCode: am.manifest.emcPlantCode,
                }),
                ghost: am.manifest.ghost === true ? "True" : "False",
            })
        );
        return (
            <Panel collapsible defaultExpanded header="Manifests">
                {errors.selectedManifests && (
                    <Alert bsStyle="danger">{errors.selectedManifests}</Alert>
                )}
                <div>
                    <BootstrapTable
                        data={flattenedAbnManifests}
                        maxHeight={1080}
                        hover
                        scrollTop="Top"
                        tableStyle={
                            errors.abnManifests && {
                                border: '#CF142B 2px solid',
                            }
                        }
                        multiColumnSort={2}
                        options={{
                            onCellEdit: isEditable ? handleCellEdit : null,
                            expandRowBgColor: '#eee',
                            expandBy: 'column',
                            noDataText: isFetching ? (
                                <SmallLoader />
                            ) : (
                                'No data to display. Please refine your search.'
                            ),
                        }}
                        cellEdit={
                            isEditable
                                ? {
                                      mode: 'click',
                                      nonEditableRows: () =>
                                          nonEditableRowsFilter(
                                              abnormality,
                                              abnManifests,
                                              selectedManifestIds
                                          ),
                                      blurToSave: true,
                                  }
                                : {}
                        }
                        selectRow={
                            isEditable && isSelectable
                                ? {
                                      mode: 'checkbox',
                                      onSelect: handleManifestSelection,
                                      onSelectAll: handleManifestSelectionAll,
                                      selected: selectedManifestIds,
                                      clickToSelect: true,
                                      clickToExpand: true,
                                  }
                                : {}
                        }
                        expandableRow={() => true}
                        expandComponent={kanbanTable(kanbanTableOptions)}
                    >
                        <TableHeaderColumn dataField="manifestId" isKey hidden>
                            ID
                        </TableHeaderColumn>
                        <TableHeaderColumn
                            dataField="manifestNumber"
                            dataFormat={(cell, row) => manifestNumberFormatter(cell, row)}
                            editable={false}
                            expandable={false}
                            dataSort
                            filter={{ type: 'TextFilter', delay: 0 }}
                        >
                            Manifest
                        </TableHeaderColumn>
                        <TableHeaderColumn
                            dataField="kanbans"
                            dataFormat={numberOfKanbansFormatter(
                                selectedManifestIds,
                                selectedKanbanIds
                            )}
                            tdStyle={isSelectable ? { cursor: 'pointer' } : {}}
                            editable={false}
                            expandable
                            width="100"
                        >
                            Kanbans
                        </TableHeaderColumn>
                        <TableHeaderColumn
                            dataField="kanbans"
                            dataFormat={impactedKanbanBoxesFormatter(
                                selectedManifestIds,
                                selectedKanbanIds
                            )}
                            editable={false}
                            expandable
                        >
                            Impacted # of boxes
                        </TableHeaderColumn>
                        <TableHeaderColumn
                            dataField="supplierCode"
                            dataFormat={(cell, row) =>
                                formatSupplier({
                                    supplierCode: row.manifest.supplierCode,
                                    supplierPlantCode: row.manifest.supplierPlantCode,
                                })
                            }
                            editable={false}
                            expandable={false}
                            dataSort
                            filter={{ type: 'TextFilter', delay: 0 }}
                        >
                            Supplier
                        </TableHeaderColumn>
                        <TableHeaderColumn
                            dataField="emc"
                            editable={false}
                            expandable={false}
                            width="130"
                            dataSort
                            filter={{ type: 'TextFilter', delay: 0 }}
                        >
                            EMC
                        </TableHeaderColumn>
                        <TableHeaderColumn
                            dataField="emcDockCode"
                            editable={false}
                            expandable={false}
                            width="130"
                            dataSort
                            filter={{ type: 'TextFilter', delay: 0 }}
                        >
                            Dock
                        </TableHeaderColumn>
                        <TableHeaderColumn 
                            dataField="ghost"
                            editable={false} 
                            expandable={false}
                            width="130"
                            dataSort
                            filter={{ type: 'TextFilter', delay: 0}}
                        > 
                            Ghost
                        </TableHeaderColumn>
                        <TableHeaderColumn
                            dataField="numberOfPallets"
                            editable={{
                                validator: palletsValidator,
                            }}
                            expandable={false}
                            dataFormat={(cell, row) =>
                                palletsFormatter(cell, row, selectedManifestIds, isEditable)
                            }
                            width="175"
                        >
                            Impacted # of pallets
                        </TableHeaderColumn>
                        <TableHeaderColumn
                            dataField="manifest"
                            dataFormat={(cell, row) => recoveryActionsFormatter(cell, row)}
                            editable={false}
                            expandable={false}
                        >
                            Existing recovery actions
                        </TableHeaderColumn>
                        <TableHeaderColumn
                            dataField="abnormalities"
                            dataFormat={(cell, row) => abnormalitiesFormatter(cell, row)}
                            editable={false}
                            expandable={false}
                        >
                            Existing abnormalities
                        </TableHeaderColumn>
                    </BootstrapTable>
                    {isEditable && (
                        <p className="help-block-larger">
                            <Glyphicon
                                glyph="info-sign"
                                className="text-primary"
                                style={{ margin: '20px 5px 0 0' }}
                            />{' '}
                            Click on a row's 'Kanbans' cell to see all kanbans.
                            <br />
                            <Glyphicon
                                glyph="exclamation-sign"
                                className="text-danger"
                                style={{ margin: '20px 5px 0 0' }}
                            />{' '}
                            Impacted # of pallets can't be changed once a manifest has assigned,
                            executed or cancelled recovery actions.
                        </p>
                    )}
                </div>
            </Panel>
        );
    }
}

ManifestsInfoGeneric.propTypes = {
    isEditable: PropTypes.bool,
    isSelectable: PropTypes.bool,
    onCheckSelect: PropTypes.func,
    onCheckSelectAll: PropTypes.func,
    selectedManifestIds: PropTypes.array,
    validations: PropTypes.object,
};

export default ManifestsInfoGeneric;
